import { useState } from 'react';
import { ChevronRight, LogOut, FolderOpen, ChevronDown, Home } from 'lucide-react';
import type { Project } from '../types/project';

interface Category {
  id: string;
  name: string;
}

interface CategorySelectionProps {
  categories: Category[];
  projects?: Project[];
  activeProjectId?: string;
  activeProject?: Project;
  onSelectCategory: (categoryId: string) => void;
  onSelectProject?: (projectId: string) => void;
  onGoHome?: () => void;
  onLogout: () => void;
  userRole?: string | null;
  userPersonName?: string | null;
}

export function CategorySelection({
  categories,
  projects = [],
  activeProjectId,
  activeProject,
  onSelectCategory,
  onSelectProject,
  onGoHome,
  onLogout,
  userRole,
  userPersonName,
}: CategorySelectionProps) {
  const [showProjectPicker, setShowProjectPicker] = useState(false);

  // Safety check - ensure categories exist
  if (!categories || categories.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div />
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Categories Available</h2>
            <p className="text-gray-600">Please contact your administrator to set up categories.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Top bar */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            {onGoHome && (
              <button
                onClick={onGoHome}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
              >
                <Home className="w-5 h-5" />
                <span>Home</span>
              </button>
            )}
            {userRole && (
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
                <span className="text-sm text-gray-600">Logged in as</span>
                <span className="text-sm font-semibold text-indigo-600">
                  {userPersonName && userPersonName !== userRole ? `${userPersonName} (${userRole})` : userRole}
                </span>
              </div>
            )}
          </div>
          {!userRole && !onGoHome && <div />}
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-white rounded-lg transition shadow-sm hover:shadow-md bg-white/80"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {/* Active Project Banner */}
        {activeProject && (
          <div className="mb-8 relative">
            <button
              onClick={() => projects.length > 1 && setShowProjectPicker(!showProjectPicker)}
              className={`w-full flex items-center gap-3 px-5 py-4 bg-white rounded-xl shadow-sm border border-indigo-100 text-left transition ${projects.length > 1 ? 'hover:border-indigo-300 hover:shadow-md cursor-pointer' : 'cursor-default'}`}
            >
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FolderOpen className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5">Reporting for</div>
                <div className="font-semibold text-gray-900">{activeProject.name}</div>
                <div className="text-xs text-gray-500 font-mono">{activeProject.number}</div>
              </div>
              {projects.length > 1 && (
                <div className="flex items-center gap-1 text-xs text-indigo-600 font-medium">
                  Switch <ChevronDown className="w-4 h-4" />
                </div>
              )}
            </button>

            {/* Project Picker Dropdown */}
            {showProjectPicker && projects.length > 1 && (
              <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                {projects.map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      onSelectProject?.(p.id);
                      setShowProjectPicker(false);
                    }}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-indigo-50 transition ${p.id === activeProjectId ? 'bg-indigo-50' : ''}`}
                  >
                    <FolderOpen className="w-4 h-4 text-indigo-500" />
                    <div>
                      <div className="font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{p.number}</div>
                    </div>
                    {p.id === activeProjectId && (
                      <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">Active</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Select a Category</h1>
          <p className="text-gray-600">Choose a category to view its tasks</p>
        </div>

        {/* Category List */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="space-y-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => onSelectCategory(category.id)}
                className="w-full flex items-center justify-between px-6 py-4 border border-gray-300 rounded-lg transition duration-200 hover:border-indigo-400 hover:bg-indigo-50 group"
              >
                <span className="text-gray-900 font-medium text-left">{category.name}</span>
                <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-indigo-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}